import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdSearch, 
  MdEdit, 
  MdDelete, 
  MdError, 
  MdFilterList, 
  MdCalendarToday, 
  MdCategory,
  MdInventory,
  MdLocationOn,
  MdClose,
  MdSave,
  MdGrade,
  MdNumbers,
  MdVisibility,
  MdInfo,
  MdGetApp
} from "react-icons/md";
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from 'assets/img/auth/usibras_ghana_limited_logo.jpeg';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' for largest to least, 'asc' for least to largest
  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Reset to first page when sort order changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortOrder]);

  // Fetch data from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-finished-products');
        if (response.data && response.data.data) {
          setProducts(response.data.data);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Error loading products');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  // Calculate summary statistics
  const calculateSummary = (data) => {
    const totalQuantity = data.reduce((sum, item) => sum + (parseInt(item.actual_stock) || 0), 0);
    
    // Calculate highest and lowest product types
    const productTypeQuantities = data.reduce((acc, item) => {
      acc[item.product_type] = parseInt(item.actual_stock) || 0;
      return acc;
    }, {});
    
    const sortedProductTypes = Object.entries(productTypeQuantities)
      .sort(([,a], [,b]) => b - a);
    
    // Get all product types with highest quantity
    const highestQuantity = sortedProductTypes[0]?.[1] || 0;
    const highestProductTypes = sortedProductTypes
      .filter(([, quantity]) => quantity === highestQuantity)
      .map(([type, quantity]) => ({ type, quantity }));

    // Get all product types with lowest quantity
    const lowestQuantity = sortedProductTypes[sortedProductTypes.length - 1]?.[1] || 0;
    const lowestProductTypes = sortedProductTypes
      .filter(([, quantity]) => quantity === lowestQuantity)
      .map(([type, quantity]) => ({ type, quantity }));

    return {
      totalQuantity,
      highestProductTypes,
      lowestProductTypes
    };
  };

  // Apply search and sort
  const filteredData = products.filter(item => {
    const searchTermLower = searchTerm.toLowerCase();
    const searchableValues = [
      item.product_type,
      item.actual_stock?.toString(),
    ].filter(Boolean);

    return searchTermLower === '' || 
      searchableValues.some(value => 
        value.toLowerCase().includes(searchTermLower)
      );
  }).sort((a, b) => {
    const quantityA = parseInt(a.actual_stock) || 0;
    const quantityB = parseInt(b.actual_stock) || 0;
    
    if (sortOrder === 'desc') {
      return quantityB - quantityA; // Largest to least
    } else {
      return quantityA - quantityB; // Least to largest
    }
  });

  const summary = calculateSummary(filteredData);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );



  // Add handleView function
  const handleView = (productType) => {
    navigate(`/admin/finished-product-warehouse/${encodeURIComponent(productType)}`);
  };

  // Add handleEdit function
  const handleEdit = (id) => {
    // TODO: Implement edit functionality
    // For now, just show a toast message
    toast.success('Edit functionality coming soon!');
    console.log('Edit product with ID:', id);
  };

  // Add handleDelete function
  const handleDelete = async (id) => {
    try {
      // TODO: Replace with actual API endpoint
      // const response = await axios.delete(`YOUR_API_ENDPOINT/${id}`);
      
      // For now, just show a success message
      toast.success('Product deleted successfully!');
      
      // Update the products list by removing the deleted item
      setProducts(prevProducts => prevProducts.filter(product => product.id !== id));
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product. Please try again.');
    }
  };

  // Download PDF function
  const downloadPdf = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 10;

    // Logo
    const img = new window.Image();
    img.src = logo;
    doc.addImage(img, 'JPEG', 10, y, 30, 30);

    // Title and Date
    doc.setFontSize(18);
    doc.text('Finished Products Report', pageWidth / 2, y + 10, { align: 'center' });
    doc.setFontSize(12);
    doc.text('Product Type and Quantity Summary', pageWidth / 2, y + 18, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-CA')}`, pageWidth / 2, y + 26, { align: 'center' });

    // Summary information
    doc.setFontSize(12);
    doc.text(`Total Products: ${summary.totalQuantity} boxes`, 14, y + 40);
    doc.text(`Total Product Types: ${filteredData.length}`, 14, y + 48);

    // Products table
    autoTable(doc, {
      startY: y + 60,
      head: [['Product Type', 'Quantity (boxes)']],
      body: filteredData.map(item => [
        item.product_type,
        item.actual_stock
      ]),
      foot: [['Total', summary.totalQuantity]],
      headStyles: { fillColor: [22, 160, 133] },
      footStyles: { fillColor: [22, 160, 133], textColor: [255, 255, 255], fontStyle: 'bold' },
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 5,
      },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 60, halign: 'right' }
      }
    });

    // Add summary at the bottom
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text(`Report generated on: ${new Date().toLocaleString()}`, 14, finalY);
    doc.text(`Data source: Finished Products Warehouse`, 14, finalY + 8);

    doc.save('finished-products-report.pdf');
  };

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-4"}>
        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                <MdInventory className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Products</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalQuantity} boxes</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-green-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Highest Product Type</p>
                <div className="mt-1">
                  <p className="text-xl font-bold text-navy-700 dark:text-white">
                    {summary.highestProductTypes.map((product, index) => (
                      <span key={index}>
                        {product.type}
                        {index < summary.highestProductTypes.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {summary.highestProductTypes.reduce((sum, product) => sum + product.quantity, 0)} boxes total
                  </p>
                </div>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-orange-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Lowest Product Type</p>
                <div className="mt-1">
                  <p className="text-xl font-bold text-navy-700 dark:text-white">
                    {summary.lowestProductTypes.map((product, index) => (
                      <span key={index}>
                        {product.type}
                        {index < summary.lowestProductTypes.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {summary.lowestProductTypes.reduce((sum, product) => sum + product.quantity, 0)} boxes total
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters and Download */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex items-center gap-2">
              <MdSearch className="h-5 w-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              />
            </div>
            <div className="flex items-center gap-2">
              <MdFilterList className="h-5 w-5 text-gray-500" />
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="desc">Largest to Least</option>
                <option value="asc">Least to Largest</option>
              </select>
            </div>
            <div className="flex items-center justify-end">
              <button
                onClick={downloadPdf}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                disabled={loading || filteredData.length === 0}
              >
                <MdGetApp className="mr-2" size={20} />
                <span>Download PDF</span>
              </button>
            </div>
          </div>
        </div>

        {/* Products Table */}
        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-navy-700">
                  <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Product Type</th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Quantity</th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <tr key={item.id} className="border-b border-gray-200 dark:border-navy-700">
                    <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">
                      <div className="flex items-center gap-2">
                        <MdCategory className="h-4 w-4 text-blue-500" />
                        <span>{item.product_type}</span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <MdInventory className="h-4 w-4 text-green-500" />
                        <span>{item.actual_stock} boxes</span>
                      </div>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleView(item.product_type)}
                          className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-3 py-2 text-sm font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-600 dark:hover:bg-blue-700"
                        >
                          <MdVisibility className="h-4 w-4" />
                          View Details
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* No Results Message */}
        {!loading && !error && paginatedData.length === 0 && (
          <div className="mt-8 text-center">
            <MdInfo className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-lg font-medium text-gray-600 dark:text-gray-400">
              No products found matching your criteria
            </p>
          </div>
        )}

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm font-medium text-navy-700 dark:text-white">
            Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Previous
            </button>
            
            {/* Page number buttons */}
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = index + 1;
              } else if (currentPage <= 3) {
                pageNum = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + index;
              } else {
                pageNum = currentPage - 2 + index;
              }
              
              return (
                <button
                  key={index}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                    currentPage === pageNum
                      ? "bg-brand-700 text-white"
                      : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Last
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProductList; 